// @ts-strict-ignore
import React from 'react';
import { getCalendarEventDelayInfo } from '@/components/sharedComponents/ReleaseDelayDescription';
import { ReleaseComponentProps } from '../typescriptHelpers';
import Popover from 'antd/lib/popover';
import Tag from 'antd/lib/tag';
import { antGrey, getAvatarColorForName, runwayColors, tagStyle } from '../components/styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Icon, { CheckCircleFilled, FireFilled, UserOutlined } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import {
    AutomationType,
    AutoReleaseStrategy,
    AutoSubmitStrategiesOnOrAfterTargetDate,
    LifecycleEventType,
    ReleaseCalendarEventType,
    AutomationRun,
    ReleaseStatus,
} from '@/types';
import Avatar from 'antd/lib/avatar';
import {
    displayEventManuallyUpdatedBy,
    displayLifecycleEventFormattedTitle,
    displayUserNameShort,
    formatDateString,
    formatEndDateStringForReleaseCalendar,
    getCalendarEventDescription,
    getCalendarEventPopoverIcon,
    isBlackoutPeriodEventType,
} from '@/helpers/displayHelpers';
import Divider from 'antd/lib/divider';
import { dayjs } from '@/extendedDayjs';
import Space from 'antd/lib/space';
import MarkdownComponent from './HOCs/Markdown';
import {
    findLifecycleAutomationRun,
    smallAutomationIconForStatus,
    smallIconColorForStatus,
} from '../components/automations/automationHelperComponents';
import { getStepForLifecycleEvent, ReleaseCalendarLifecycleEventData } from '../helpers';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAutomationsById, selectScheduleSettings, selectUserById } from '@/selectors/selectors';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

interface ReleaseCyclePopoverProps extends ReleaseComponentProps {
    parentBackgroundColor: string;
    backgroundColor: string;
    eventContent: React.ReactNode;
    displayVersion: string;
    isHotfix: boolean;
    calendarEvents: ReleaseCalendarLifecycleEventData[];
    releasePilotId?: string;
    releaseDescription?: string;
    lifecycleAutomations: AutomationRun[];
    start: string;
    end?: string;
    isSkippedRelease: boolean;
}

const ReleaseCyclePopover: React.FC<ReleaseCyclePopoverProps> = (props: ReleaseCyclePopoverProps) => {
    const autoSubmitAutomation =
        props.lifecycleAutomations && props.lifecycleAutomations[AutomationType.AutoSubmitRelease] ?
            props.lifecycleAutomations[AutomationType.AutoSubmitRelease]
        :   null;
    const autoReleaseAutomation =
        props.lifecycleAutomations && props.lifecycleAutomations[AutomationType.AutoRelease] ?
            props.lifecycleAutomations[AutomationType.AutoRelease]
        :   null;

    const scheduleCadenceSettings = useSelector((state: RootState) => selectScheduleSettings(state, props.appId));

    const releasePilotUser = useSelector((state: RootState) => {
        return selectUserById(state, props.orgId, props.appId, props.releasePilotId);
    });

    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, props.appId));

    const filteredEvents = props.calendarEvents.filter(
        (event) =>
            event.eventType != LifecycleEventType.LifecycleEventTypeRolloutIncrement &&
            !isBlackoutPeriodEventType(event.eventType),
    );

    const title = (
        <div
            style={{
                margin: 0,
                padding: 8,
                backgroundColor: props.parentBackgroundColor,
                width: '100%',
                height: '100%',
                boxSizing: 'border-box',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <Tag color={runwayColors.white} style={{ ...tagStyle, border: `1px solid ${runwayColors.white}` }}>
                    <Text style={{ color: props.parentBackgroundColor }}>{props.displayVersion}</Text>
                </Tag>
                {props.isHotfix && (
                    <Tag
                        color={props.parentBackgroundColor}
                        style={{ ...tagStyle, border: `1px solid ${runwayColors.white}` }}
                    >
                        <FireFilled />
                        <Text
                            style={{
                                color: runwayColors.white,
                                fontWeight: 600,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                fontSize: 10,
                                lineHeight: '23px',
                            }}
                        >
                            Hotfix
                        </Text>
                    </Tag>
                )}
                {props.isSkippedRelease && (
                    <Tag
                        color={props.parentBackgroundColor}
                        style={{ ...tagStyle, border: `1px dashed ${runwayColors.white}` }}
                    >
                        <Text
                            style={{
                                color: runwayColors.white,
                                fontWeight: 600,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                fontSize: 10,
                                lineHeight: '23px',
                            }}
                        >
                            Skipped
                        </Text>
                    </Tag>
                )}
                {props.start && props.end && (
                    <Text
                        style={{
                            color: runwayColors.white,
                            fontSize: 16,
                            fontWeight: 'normal',
                            textDecoration: props.isSkippedRelease ? 'line-through' : '',
                        }}
                    >
                        {formatDateString(props.start, 'MMM D')} -{' '}
                        {formatEndDateStringForReleaseCalendar(props.end, 'MMM D')}
                    </Text>
                )}
            </span>
        </div>
    );

    const renderEventDates = (eventList: ReleaseCalendarLifecycleEventData[]) => {
        return (
            <div
                style={{
                    backgroundColor: antGrey.grey2,
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${antGrey.grey5}`,
                }}
            >
                <Space
                    split={<Divider style={{ backgroundColor: antGrey.grey5, margin: 0 }} />}
                    direction="vertical"
                    size={12}
                    style={{ width: '100%' }}
                >
                    {eventList.map((event, index) => {
                        const submitEnabledNoEarlierThanTarget =
                            event.eventType === LifecycleEventType.LifecycleEventTypeSubmit &&
                            autoSubmitAutomation &&
                            autoSubmitAutomation.enabled &&
                            AutoSubmitStrategiesOnOrAfterTargetDate.includes(autoSubmitAutomation.data.submitStrategy);
                        const releaseEnabledNoEarlierThanTarget =
                            event.eventType === LifecycleEventType.LifecycleEventTypeRelease &&
                            autoReleaseAutomation &&
                            autoReleaseAutomation.enabled &&
                            autoReleaseAutomation.data.strategy ===
                                AutoReleaseStrategy.OnceStepsCompleteAfterTargetDate;
                        const icon = getCalendarEventPopoverIcon(
                            event.eventType,
                            event.eventDateType,
                            event.start,
                            submitEnabledNoEarlierThanTarget || releaseEnabledNoEarlierThanTarget,
                            event.targetDateSetByCadence,
                        );
                        const description = getCalendarEventDescription(
                            event.eventType,
                            event.eventDateType,
                            submitEnabledNoEarlierThanTarget || releaseEnabledNoEarlierThanTarget,
                            scheduleCadenceSettings,
                            event.targetDateSetByCadence,
                            event.status === ReleaseStatus.Skipped,
                        );
                        const delayInfo = getCalendarEventDelayInfo(
                            event.eventType,
                            event.eventDateType,
                            event.status === ReleaseStatus.Skipped,
                            event.delayMinutes,
                            event.delayHasStarted,
                            event.isManualDelay,
                            props.orgId,
                            props.appId,
                        );

                        const showManualUpdate =
                            event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate &&
                            !!event.targetDateManuallyUpdatedAt;
                        const manualUpdatedByUser = appUsers.find(
                            (user) => user.id === event.targetDateManuallyUpdatedByUserId,
                        );
                        const manualUpdateText = displayEventManuallyUpdatedBy(
                            event.targetDateManuallyUpdatedAt,
                            manualUpdatedByUser,
                        );
                        const textStyle = {
                            fontSize: '15px',
                            color:
                                event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ?
                                    antGrey.grey7
                                :   antGrey.grey8,
                            fontWeight: 200,
                        };

                        const formattedDateString = formatDateString(event.start, 'ddd, MMM D / h:mma');
                        let date = <Text style={textStyle}>{formattedDateString}</Text>;
                        if (
                            props.isSkippedRelease &&
                            (event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ||
                                event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate)
                        ) {
                            const dateString = formatDateString(event.start, 'ddd, MMM D');
                            const timeString = formatDateString(event.start, 'h:mma');
                            date = (
                                <>
                                    <Text delete style={textStyle}>
                                        {dateString}
                                    </Text>
                                    {'\u00A0'}
                                    <Text style={textStyle}>{'/'}</Text>
                                    {'\u00A0'}
                                    <Text delete style={textStyle}>
                                        {timeString}
                                    </Text>
                                </>
                            );
                        }

                        return (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: description ? 'start' : 'center',
                                    width: '400px',
                                }}
                            >
                                <Space size={0}>
                                    <Text
                                        style={{
                                            fontSize: '12px',
                                            color: antGrey.grey7,
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            marginRight: 8,
                                            fontWeight: 400,
                                        }}
                                    >
                                        {displayLifecycleEventFormattedTitle(
                                            event.eventType,
                                            event.eventDateType,
                                            event.isEventComplete,
                                        )}
                                    </Text>
                                    <LifecycleEventIcon
                                        appId={props.appId}
                                        eventType={event.eventType}
                                        eventDataType={event.eventDateType}
                                        dateString={event.start}
                                        isEventComplete={event.isEventComplete}
                                    />
                                </Space>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end', gap: 4 }}>
                                    <Space size={0}>
                                        {showManualUpdate && (
                                            <Tooltip title={manualUpdateText} zIndex={100000}>
                                                <UserOutlined
                                                    style={{ color: antGrey.grey7, marginRight: 8, fontSize: 16 }}
                                                />
                                            </Tooltip>
                                        )}
                                        {icon && (
                                            <Icon
                                                component={icon}
                                                style={{
                                                    color: antGrey.grey7,
                                                    marginRight: 8,
                                                    fontSize: 16,
                                                }}
                                            />
                                        )}
                                        {date}
                                        {delayInfo && (
                                            <TooltipInfoIcon
                                                tooltipContent={delayInfo}
                                                style={{ marginLeft: 8 }}
                                                tooltipProps={{ zIndex: 100000 }}
                                            />
                                        )}
                                    </Space>
                                    {description && (
                                        <Text style={{ fontSize: 12, color: antGrey.grey6, textAlign: 'right' }}>
                                            {description}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </Space>
            </div>
        );
    };

    const content = (
        <div style={{ padding: 10, paddingBottom: 20, margin: 10, opacity: props.isSkippedRelease ? 0.55 : 1 }}>
            {renderEventDates(filteredEvents)}
            {releasePilotUser && (
                <>
                    <div style={{ marginTop: 15, marginBottom: 6 }}>
                        <Text
                            style={{
                                fontSize: '12px',
                                color: antGrey.grey7,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                marginRight: 8,
                                fontWeight: 400,
                            }}
                        >
                            Release Pilot
                        </Text>
                    </div>
                    <div style={{ marginBottom: 15 }}>
                        <Avatar
                            key={releasePilotUser.id}
                            src={releasePilotUser.commsAvatars?.image32}
                            style={{
                                backgroundColor: getAvatarColorForName(displayUserNameShort(releasePilotUser)),
                                marginRight: 10,
                                fontSize: 12,
                            }}
                            size={25}
                        >
                            {displayUserNameShort(releasePilotUser).charAt(0).toUpperCase()}
                        </Avatar>
                        <Text>{displayUserNameShort(releasePilotUser)}</Text>
                    </div>
                </>
            )}
            {props.releaseDescription && (
                <div>
                    <Divider style={{ marginTop: 10, marginBottom: 10, borderColor: antGrey.grey5 }} />
                    <div style={{ marginBottom: 6 }}>
                        <Text
                            style={{
                                fontSize: '12px',
                                color: antGrey.grey7,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                marginRight: 8,
                                fontWeight: 400,
                            }}
                        >
                            Release Description
                        </Text>
                    </div>
                    <div
                        style={{
                            maxWidth: '300px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                            paddingRight: 4,
                            overflowWrap: 'break-word',
                        }}
                    >
                        <MarkdownComponent source={props.releaseDescription} />
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Popover
            content={content}
            title={title}
            arrow={false}
            overlayStyle={{
                padding: 0,
                margin: 0,
                zIndex: 99999,
            }}
            overlayClassName="release-cycle-popover"
        >
            {props.eventContent}
        </Popover>
    );
};

export default ReleaseCyclePopover;

const LifecycleEventIcon: React.FC<{
    appId: string;
    eventType: LifecycleEventType;
    eventDataType: ReleaseCalendarEventType;
    dateString: string;
    isEventComplete: boolean;
    lifecycleAutomations?: AutomationRun[];
}> = ({ appId, eventType, eventDataType, dateString, isEventComplete, lifecycleAutomations }) => {
    const automationsById = useSelector((state: RootState) => selectAutomationsById(state, appId));

    if (eventDataType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate && isEventComplete) {
        return <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />;
    }

    if (lifecycleAutomations) {
        const automationRun = findLifecycleAutomationRun(getStepForLifecycleEvent(eventType), lifecycleAutomations);
        const automationConfigEnabled = automationsById[automationRun.id].enabled;

        const automationEnabled =
            automationRun && automationConfigEnabled && dayjs(dateString)?.isSame(automationRun.scheduledAt, 'day');
        const automation = automationEnabled ? automationRun : null;
        if (automationEnabled && dayjs(dateString)) {
            const color = smallIconColorForStatus(automation.status);
            return <>{smallAutomationIconForStatus(automation.status, color)}</>;
        }
    }

    return <></>;
};
